import React, { type Dispatch, type SetStateAction } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { soxProcessEditPath } from '../../routing/paths.ts';
import { buildProcessReturnState } from '../../routing/processNavigation.ts';
import { processIdForEditRoute, processUuidForApi } from './functional.js';
import { Pencil, Trash2 } from 'lucide-react';
import type { UseMutationResult } from '@tanstack/react-query';
import type { TableColumnDef } from '../../shared/table/types.ts';
import AssociationCountCell from '../risks-table/components/AssociationCountCell.tsx';
import { formatProcessNameForList, toTagList } from './functional.js';
import { PROCESS_SOURCE } from './schema.js';
import type { ProcessListPopoverState } from './useProcessListPopover.ts';

export type ConfigureProcessRow = {
  id?: string;
  process_uuid?: string;
  process_id?: string;
  name?: string | null;
  description?: string | null;
  owner?: string | null;
  actors?: string[] | string | null;
  systems?: string[] | string | null;
  source?: string | null;
};

export type ConfigureProcessesColumnsOptions = {
  /** Path to return to from process edit (e.g. current processes list URL). */
  processReturnPath: string;
  /** When set, delete icon opens confirmation (parent owns modal) instead of deleting immediately. */
  onDeleteRequest?: (row: ConfigureProcessRow) => void;
};

export const splitCommaList = (value: unknown): string[] =>
  String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

export const sourceLabel = (source: unknown): string => {
  const normalized = String(source || '').toLowerCase();
  if (normalized === PROCESS_SOURCE.AI_ASSISTED) return 'AI Assisted';
  if (normalized === PROCESS_SOURCE.BATCH_IMPORT) return 'Batch Import';
  return 'Manual';
};

export function createConfigureProcessesColumns(
  cycleId: string,
  navigate: NavigateFunction,
  deleteMutation: UseMutationResult<unknown, unknown, string, unknown>,
  setListPopover: Dispatch<SetStateAction<ProcessListPopoverState | null>>,
  options: ConfigureProcessesColumnsOptions
): TableColumnDef<ConfigureProcessRow>[] {
  const { processReturnPath, onDeleteRequest } = options;
  const processEditRouteId = (row: ConfigureProcessRow) => processIdForEditRoute(row);
  const processApiUuid = (row: ConfigureProcessRow) => processUuidForApi(row);

  return [
    {
      id: 'name',
      columnLabel: 'Process Name',
      header: () => <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Process Name</span>,
      cell: ({ row }) => {
        const full = row.original.name ?? '—';
        const shown = formatProcessNameForList(full);
        return (
          <span className="text-sm font-medium text-gray-900" title={full !== shown ? full : undefined}>
            {shown}
          </span>
        );
      },
    },
    {
      id: 'description',
      columnLabel: 'Description',
      header: () => <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Description</span>,
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-md text-sm text-gray-700">{row.original.description || '—'}</span>
      ),
    },
    {
      id: 'owner',
      columnLabel: 'Owner',
      header: () => <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Owner</span>,
      cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.owner || '—'}</span>,
    },
    {
      id: 'actors',
      columnLabel: 'Actors',
      header: () => <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Actors</span>,
      cell: ({ row }) => {
        const actors = toTagList(row.original.actors);
        return (
          <AssociationCountCell
            count={actors.length}
            triggerDataAttr="data-process-list-trigger"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setListPopover({
                title: 'Actors',
                items: actors,
                top: rect.bottom + 8,
                left: Math.min(rect.left, Math.max(8, window.innerWidth - 320)),
              });
            }}
          />
        );
      },
    },
    {
      id: 'systems',
      columnLabel: 'Systems',
      header: () => <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Systems</span>,
      cell: ({ row }) => {
        const systems = toTagList(row.original.systems);
        return (
          <AssociationCountCell
            count={systems.length}
            triggerDataAttr="data-process-list-trigger"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setListPopover({
                title: 'Systems',
                items: systems,
                top: rect.bottom + 8,
                left: Math.min(rect.left, Math.max(8, window.innerWidth - 320)),
              });
            }}
          />
        );
      },
    },
    {
      id: 'source',
      columnLabel: 'Source',
      widthPercent: 12,
      header: () => <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Source</span>,
      cell: ({ row }) => (
        <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
          {sourceLabel(row.original.source)}
        </span>
      ),
    },
    {
      id: 'actions',
      columnLabel: 'Actions',
      header: () => <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Actions</span>,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded p-1.5 text-gray-600 hover:bg-gray-100"
            onClick={() => {
              const processRouteId = processEditRouteId(row.original);
              if (!processRouteId) return;
              navigate(soxProcessEditPath(cycleId, processRouteId), {
                state: buildProcessReturnState(processReturnPath),
              });
            }}
            title="Edit process"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded p-1.5 text-red-600 hover:bg-red-50"
            onClick={() => {
              if (onDeleteRequest) {
                onDeleteRequest(row.original);
                return;
              }
              const id = processApiUuid(row.original);
              if (id) deleteMutation.mutate(id);
            }}
            title="Delete process"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];
}
